import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons for webpack bundler
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl:       require("leaflet/dist/images/marker-icon.png"),
  shadowUrl:     require("leaflet/dist/images/marker-shadow.png"),
});

const DEFAULT_CENTER: [number, number] = [10.7769, 106.7009];

interface MapPickerProps {
  lat: string | number;
  lng: string | number;
  onChange: (coords: { lat: string; lng: string }) => void;
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  type: string;
}

async function reverseGeocode(la: number, lo: number): Promise<string | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${la}&lon=${lo}&format=json`,
      { headers: { "Accept-Language": "vi,en;q=0.9" } }
    );
    const data = await res.json();
    return data?.display_name ?? null;
  } catch {
    return null;
  }
}

export default function MapPicker({ lat, lng, onChange }: MapPickerProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef       = useRef<L.Map | null>(null);
  const markerRef    = useRef<L.Marker | null>(null);

  const [query,       setQuery]       = useState<string>("");
  const [results,     setResults]     = useState<NominatimResult[]>([]);
  const [searching,   setSearching]   = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [reversing,   setReversing]   = useState<boolean>(false);

  const searchDebounce  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reverseDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // true = lat/lng changed because of map/search click (not manual typing)
  const fromMapRef      = useRef<boolean>(false);

  // ── Đặt / di chuyển marker ────────────────────────────────────────────────
  const placeMarker = useCallback((map: L.Map, la: number, lo: number) => {
    if (markerRef.current) {
      markerRef.current.setLatLng([la, lo]);
    } else {
      const m = L.marker([la, lo], { draggable: true }).addTo(map);
      m.on("dragend", () => {
        const p = m.getLatLng();
        fromMapRef.current = false; // drag → do reverse geocode
        onChange({ lat: p.lat.toFixed(7), lng: p.lng.toFixed(7) });
      });
      markerRef.current = m;
    }
  }, [onChange]);

  // ── Init map (runs once on mount) ─────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const la = parseFloat(String(lat));
    const lo = parseFloat(String(lng));
    const hasCoords = !isNaN(la) && !isNaN(lo) && lat !== "" && lng !== "";
    const center: [number, number] = hasCoords ? [la, lo] : DEFAULT_CENTER;

    const map = L.map(containerRef.current, { center, zoom: hasCoords ? 16 : 13 });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      fromMapRef.current = true;
      placeMarker(map, e.latlng.lat, e.latlng.lng);
      onChange({ lat: e.latlng.lat.toFixed(7), lng: e.latlng.lng.toFixed(7) });
      // Reverse geocode khi click bản đồ
      setReversing(true);
      reverseGeocode(e.latlng.lat, e.latlng.lng).then((name) => {
        if (name) setQuery(name);
        setReversing(false);
      });
    });

    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 150);

    // Nếu có tọa độ sẵn (khi edit) → đặt marker + reverse geocode
    if (hasCoords) {
      placeMarker(map, la, lo);
      setReversing(true);
      reverseGeocode(la, lo).then((name) => {
        if (name) setQuery(name);
        setReversing(false);
      });
    }

    return () => {
      map.remove();
      mapRef.current    = null;
      markerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Xóa marker khi lat/lng bị clear ──────────────────────────────────────
  useEffect(() => {
    if (lat || lng) return;
    markerRef.current?.remove();
    markerRef.current = null;
    setQuery("");
  }, [lat, lng]);

  // ── Khi lat/lng thay đổi từ input thủ công ────────────────────────────────
  useEffect(() => {
    const la = parseFloat(String(lat));
    const lo = parseFloat(String(lng));
    if (isNaN(la) || isNaN(lo) || lat === "" || lng === "") return;
    if (fromMapRef.current) { fromMapRef.current = false; return; }

    // Di chuyển map + marker
    if (mapRef.current) {
      mapRef.current.setView([la, lo], 17, { animate: true });
      placeMarker(mapRef.current, la, lo);
    }

    // Debounce reverse geocode
    if (reverseDebounce.current) clearTimeout(reverseDebounce.current);
    reverseDebounce.current = setTimeout(() => {
      setReversing(true);
      reverseGeocode(la, lo).then((name) => {
        if (name) { setQuery(name); setShowResults(false); }
        setReversing(false);
      });
    }, 800);
  }, [lat, lng, placeMarker]);

  // ── Search (debounce 600ms) ────────────────────────────────────────────────
  const handleSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setShowResults(false);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (val.trim().length < 3) { setResults([]); return; }

    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const headers = { "Accept-Language": "vi,en;q=0.9" };
        let res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&limit=6&countrycodes=vn`,
          { headers }
        );
        let data: NominatimResult[] = await res.json();
        if (data.length === 0) {
          res = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&limit=6`,
            { headers }
          );
          data = await res.json();
        }
        setResults(data);
        setShowResults(true);
      } catch {
        setResults([]);
        setShowResults(true);
      } finally {
        setSearching(false);
      }
    }, 600);
  }, []);

  // ── Chọn kết quả tìm kiếm ─────────────────────────────────────────────────
  const handleSelectResult = useCallback((item: NominatimResult) => {
    const la = parseFloat(item.lat);
    const lo = parseFloat(item.lon);
    setQuery(item.display_name);
    setShowResults(false);
    setResults([]);
    fromMapRef.current = true; // address known, skip reverse geocode

    if (!mapRef.current) return;
    mapRef.current.setView([la, lo], 17, { animate: true });
    placeMarker(mapRef.current, la, lo);
    onChange({ lat: la.toFixed(7), lng: lo.toFixed(7) });
  }, [placeMarker, onChange]);

  return (
    <div className="space-y-2">

      {/* ── Search box ── */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={query}
              onChange={handleSearchInput}
              onFocus={() => results.length > 0 && setShowResults(true)}
              placeholder="Tìm địa chỉ... (VD: 23 Nguyễn Huệ, Quận 1)"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-300"
            />
            <span className="absolute left-3 top-2.5 text-gray-400 text-sm">
              {searching || reversing ? "⏳" : "🔍"}
            </span>
          </div>
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(""); setResults([]); setShowResults(false); }}
              className="px-3 py-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-lg text-sm"
            >
              ✕
            </button>
          )}
        </div>

        {showResults && results.length > 0 && (
          <div className="absolute z-[1000] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
            {results.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleSelectResult(item)}
                className="w-full text-left px-4 py-2.5 hover:bg-coffee-50 border-b border-gray-50 last:border-0"
              >
                <p className="text-sm text-gray-800 line-clamp-1">{item.display_name}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {parseFloat(item.lat).toFixed(5)}, {parseFloat(item.lon).toFixed(5)}
                </p>
              </button>
            ))}
          </div>
        )}

        {showResults && results.length === 0 && !searching && query.length >= 3 && (
          <div className="absolute z-[1000] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm text-gray-400">
            Không tìm thấy — thử nhập ngắn hơn hoặc nhập tọa độ thủ công
          </div>
        )}
      </div>

      {/* ── Map ── */}
      <div
        ref={containerRef}
        style={{ height: 300, borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb" }}
      />

      {/* ── Coords + clear ── */}
      <div className="flex items-center justify-between text-xs text-gray-500 px-1">
        {lat && lng ? (
          <span>📍 <span className="font-mono">{parseFloat(String(lat)).toFixed(5)}, {parseFloat(String(lng)).toFixed(5)}</span></span>
        ) : (
          <span className="text-gray-400 italic">Tìm kiếm hoặc click lên bản đồ để đặt vị trí</span>
        )}
        {lat && lng && (
          <button type="button" onClick={() => onChange({ lat: "", lng: "" })} className="text-red-400 hover:text-red-600">
            ✕ Xóa vị trí
          </button>
        )}
      </div>

      {/* ── Input tay ── */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-400 mb-0.5">Latitude</label>
          <input
            type="number" step="any" value={lat}
            onChange={(e) => onChange({ lat: e.target.value, lng: String(lng) })}
            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-coffee-400 bg-gray-50"
            placeholder="10.7769"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-0.5">Longitude</label>
          <input
            type="number" step="any" value={lng}
            onChange={(e) => onChange({ lat: String(lat), lng: e.target.value })}
            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-coffee-400 bg-gray-50"
            placeholder="106.7009"
          />
        </div>
      </div>
    </div>
  );
}
